/**
 * Time phrasing for the Today screen. Every function takes "now" as an argument — nothing in the
 * app reads the wall clock directly, because demo mode's clock is fixed and any component that
 * cheats would make the app non-deterministic (and future screenshots useless).
 */

import { daysBetween, type ISODate } from '@app/shared'

/**
 * THE LOCAL CALENDAR DAY A TIMESTAMP FELL ON, read in the reader's own zone (or an explicit one,
 * for a test — see clock.test.ts).
 *
 * `timestamp.slice(0, 10)` answers a DIFFERENT question — the UTC day — and on the US west coast
 * the two disagree for the last seven hours of every day. A task completed at 11:03 PM on
 * Wednesday carries `2026-08-28T06:03Z`; sliced, it files under Thursday, and the done log then
 * heads a completion `TOMORROW · FRI 28`. A completion cannot be tomorrow (owner report,
 * 2026-08-28). The same conversion answers the School DUE column's day half — a `due_at` a few
 * hours before local midnight is a different calendar day in UTC than it is in Los Angeles, and a
 * label that shows a local clock reading next to a UTC day can disagree with itself.
 *
 * ANYTHING THAT GROUPS OR COUNTS A STAMPED EVENT BY DAY comes through here. Plain `ISODate`
 * values are already calendar days and must not: those go through `@app/shared`'s `toDate`,
 * which pins them to noon UTC for exactly the opposite reason.
 *
 * It lives in apps/web rather than in @app/shared on purpose — "local" means the VIEWER's zone,
 * which is a browser fact. A Worker has no local day, only a deployment's.
 */
export function localDayOf(timestamp: string, timeZone: string = browserTimeZone()): ISODate {
  return localParts(timestamp, timeZone).date
}

/** '1m 04s' — the voice-memo row's duration. */
export function durationPhrase(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}m ${String(s).padStart(2, '0')}s`
}

/**
 * '1:09:00' for a lecture, '4:12' for anything under an hour.
 *
 * It lives here rather than beside the screen that first needed it because three unrelated places
 * state a recording's length now, and one of them (the recording ROW) is imported BY the screen
 * the function used to live in — which is a cycle.
 */
export function clockLength(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = Math.floor(totalSeconds % 60)
  const mm = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes)
  return `${hours > 0 ? `${hours}:` : ''}${mm}:${String(seconds).padStart(2, '0')}`
}

/** 'in 1h 19m' · 'in 12m' · 'now' · 'ended 40m ago'. */
export function untilClassPhrase(
  startMinutes: number,
  endMinutes: number,
  nowMinutes: number,
): string {
  if (nowMinutes >= startMinutes && nowMinutes < endMinutes) return 'now'
  if (nowMinutes >= endMinutes) {
    const ago = nowMinutes - endMinutes
    return ago < 60 ? `ended ${ago}m ago` : `ended ${Math.floor(ago / 60)}h ago`
  }
  const delta = startMinutes - nowMinutes
  const h = Math.floor(delta / 60)
  const m = delta % 60
  if (h === 0) return `in ${m}m`
  return m === 0 ? `in ${h}h` : `in ${h}h ${m}m`
}

/**
 * THE VIEWER'S OWN ZONE, read once per call rather than cached — a laptop that changes time zones
 * mid-session (or a test that stubs `Intl`) must see the new one on the next render, not a value
 * frozen at module load.
 */
function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * AN INSTANT'S OWN LOCAL CALENDAR DAY AND MINUTE-OF-DAY, in `timeZone` — the one conversion
 * `clockFromTimestamp` and `timeRemaining` both need and neither used to do.
 *
 * `ts.slice(11, 16)` (the bug this replaces, owner report 2026-09-10) reads the hour/minute
 * DIGITS off the string, which is only ever correct if the string's own offset already equals
 * `timeZone`. Every `due_at`/`lock_at`/`assigned_at` this app receives comes from Postgres via
 * PostgREST as UTC (`…Z`) — so the sliced digits were UTC digits printed as if they were already
 * Pacific, and a Canvas deadline at `07:59Z` (12:59 AM in Los Angeles) rendered as "7:59am", seven
 * hours later than the truth. `Intl.DateTimeFormat` with an explicit `timeZone` is the one correct
 * way to ask "what did the wall clock read, THERE, at this instant" — the same pattern
 * `apps/worker/src/lib/calendar.ts`'s `localParts()` already uses for the Worker's Calendar
 * surface; this is that pattern's web-side twin.
 *
 * A bare calendar date (no 'T', e.g. an `ISODate`) carries no instant to convert — converting it
 * anyway is actively wrong, because `new Date('2026-09-26')` parses as UTC midnight and Intl would
 * then report the PREVIOUS local day west of Greenwich. Such a string already IS the local day, by
 * definition, so it passes through unchanged with a nominal midnight minute-of-day.
 */
function localParts(ts: string, timeZone: string): { date: ISODate; minutes: number } {
  if (!ts.includes('T')) return { date: ts as ISODate, minutes: 0 }
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ts))
  const get = (type: string) => parts.find((p) => p.type === type)!.value
  const date = `${get('year')}-${get('month')}-${get('day')}` as ISODate
  const minutes = Number(get('hour')) * 60 + Number(get('minute'))
  return { date, minutes }
}

/**
 * TIME REMAINING, and its colour threshold. README §State Management lists both as "Derived,
 * never stored": overdue takes `danger-2`, anything inside a day takes `urgent-time`, and
 * everything else stays `microcopy`.
 *
 *   overdue → `−14h` / `−3d`, tone 'overdue'
 *   <24h    → `21h`,          tone 'urgent'
 *   else    → `3d`,           tone 'calm'
 *
 * Truncating rather than rounding is deliberate: a deadline 14 hours and 40 minutes gone is
 * "−14h", never "−15h". Never round a deadline towards more time than there is.
 *
 * `timeZone` defaults to the viewer's own (a test pins it explicitly — see clock.test.ts) so the
 * day-and-minute math below runs in the zone the countdown is actually read in, not UTC.
 */
export type TimeTone = 'overdue' | 'urgent' | 'calm'

export function timeRemaining(
  dueAt: string,
  today: ISODate,
  nowMinutes: number,
  timeZone: string = browserTimeZone(),
): { label: string; tone: TimeTone; minutes: number } {
  const { date: dueDate, minutes: dueMinutes } = localParts(dueAt, timeZone)
  const minutes = daysBetween(today, dueDate) * 1440 + dueMinutes - nowMinutes

  if (minutes < 0) {
    const gone = -minutes
    return {
      label: gone < 1440 ? `−${Math.floor(gone / 60)}h` : `−${Math.floor(gone / 1440)}d`,
      tone: 'overdue',
      minutes,
    }
  }
  if (minutes < 1440) return { label: `${Math.floor(minutes / 60)}h`, tone: 'urgent', minutes }
  return { label: `${Math.floor(minutes / 1440)}d`, tone: 'calm', minutes }
}

/**
 * How much of the window between hand-out and deadline has gone, 0–1 — the Today screen's
 * elapsed bar. Returns null when the assignment carries no `assignedAt`, because a bar drawn
 * from a guessed start is a lie about how much time is left.
 */
export function elapsedFraction(
  assignedAt: string | null,
  dueAt: string,
  today: ISODate,
  nowMinutes: number,
  timeZone: string = browserTimeZone(),
): number | null {
  if (!assignedAt) return null
  const left = timeRemaining(dueAt, today, nowMinutes, timeZone).minutes
  const sinceHandout = -timeRemaining(assignedAt, today, nowMinutes, timeZone).minutes
  const window = left + sinceHandout
  if (window <= 0) return null
  return Math.min(1, Math.max(0, sinceHandout / window))
}

/**
 * '11:59pm' from an ISO timestamp string, read through the viewer's own time zone (default) or
 * an explicit one — never off the string's raw digits. See `localParts` above for why.
 */
export function clockFromTimestamp(ts: string, timeZone: string = browserTimeZone()): string {
  const { minutes } = localParts(ts, timeZone)
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  const suffix = h < 12 ? 'am' : 'pm'
  const h12 = h % 12 === 0 ? 12 : h % 12
  const mStr = String(m).padStart(2, '0')
  return mStr === '00' ? `${h12}${suffix}` : `${h12}:${mStr}${suffix}`
}

/**
 * MINUTES SINCE LOCAL MIDNIGHT for an instant — the School DUE column's "in class" check needs
 * this alongside `localDayOf`, to compare a deadline against a course's meeting-time minutes in
 * the same zone its calendar day was read in, rather than mixing a local day with UTC minutes.
 */
export function localMinutesOfDay(ts: string, timeZone: string = browserTimeZone()): number {
  return localParts(ts, timeZone).minutes
}

/**
 * The app clock's `{date, minutesIntoDay}` (`useClock`) as an instant, read in this browser's
 * zone. Minute-grained, like the clock it reads: the start of the current minute.
 */
export function clockInstant(date: ISODate, minutesIntoDay: number): number {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(y!, m! - 1, d!, 0, minutesIntoDay).getTime()
}
